import React, { useState, useEffect } from 'react'
import {
  Mail, Save, TestTube, RotateCcw, Eye, EyeOff,
  AlertCircle, CheckCircle, Settings
} from 'lucide-react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import Button from '../../components/ui/Button'
import Input from '../../components/ui/Input'
import Select from '../../components/ui/Select'
import Modal from '../../components/Modal'
import { useToast } from '../../components/Toast'

import notificationsService, { NotificationConfig, TestResult } from '../../services/notifications'

const EmailPage: React.FC = () => {
  const { success, error } = useToast()
  const queryClient = useQueryClient()

  // Estados
  const [config, setConfig] = useState<NotificationConfig | null>(null)
  const [showPasswords, setShowPasswords] = useState(false)
  const [showTestModal, setShowTestModal] = useState(false)
  const [testEmail, setTestEmail] = useState('')
  const [selectedEmailProvider, setSelectedEmailProvider] = useState('custom')

  // Query
  const { data: configData, isLoading } = useQuery({
    queryKey: ['notifications-config'],
    queryFn: () => notificationsService.getConfig()
  })

  // Mutations
  const updateConfigMutation = useMutation({
    mutationFn: (newConfig: NotificationConfig) => notificationsService.updateConfig(newConfig),
    onSuccess: () => {
      success('Configuração de email salva com sucesso!')
      queryClient.invalidateQueries({ queryKey: ['notifications-config'] })
    },
    onError: (err: any) => {
      error('Erro ao salvar configuração', err.message)
    }
  })

  const testConfigMutation = useMutation({
    mutationFn: (params: { type: 'email'; email: string }) =>
      notificationsService.testConfiguration(params),
    onSuccess: (result: TestResult) => {
      if (result.success) {
        success('Teste de email realizado com sucesso!')
      } else {
        error('Falha no teste', 'Verifique as configurações e tente novamente')
      }
      setShowTestModal(false)
    },
    onError: (err: any) => {
      error('Erro ao testar configuração', err.message)
    }
  })

  const resetConfigMutation = useMutation({
    mutationFn: () => notificationsService.resetConfig(),
    onSuccess: () => {
      success('Configuração resetada com sucesso!')
      queryClient.invalidateQueries({ queryKey: ['notifications-config'] })
    },
    onError: (err: any) => {
      error('Erro ao resetar configuração', err.message)
    }
  })

  // Efeitos
  useEffect(() => {
    if (configData?.config) {
      setConfig(configData.config)
    }
  }, [configData])

  // Handlers
  const handleSave = () => {
    if (!config) return

    // Validar campos obrigatórios
    if (config.email.enabled && (!config.email.host || !config.email.user || !config.email.password)) {
      error('Configuração inválida', 'Configuração de email incompleta')
      return
    }

    updateConfigMutation.mutate(config)
  }

  const handleTest = () => {
    if (!testEmail) {
      error('Email obrigatório', 'Digite um email para teste')
      return
    }

    testConfigMutation.mutate({
      type: 'email',
      email: testEmail
    })
  }

  const handleReset = () => {
    resetConfigMutation.mutate()
  }

  const handleEmailProviderChange = (provider: string) => {
    setSelectedEmailProvider(provider)

    if (config && provider !== 'custom') {
      const providers = notificationsService.getEmailProviders()
      const providerConfig = providers[provider]

      if (providerConfig) {
        setConfig({
          ...config,
          email: {
            ...config.email,
            ...providerConfig
          }
        })
      }
    }
  }

  const updateEmailConfig = (field: keyof NotificationConfig['email'], value: any) => {
    if (!config) return
    setConfig({
      ...config,
      email: {
        ...config.email,
        [field]: value
      }
    })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">A carregar configurações...</div>
      </div>
    )
  }

  if (!config) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">Erro ao carregar configurações</div>
      </div>
    )
  }

  const emailProviders = notificationsService.getEmailProviders()

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow-sm border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Mail className="h-8 w-8 text-blue-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Integração Email</h1>
              <p className="text-gray-600">Configure o servidor SMTP para envio de emails</p>
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setShowTestModal(true)}
              leftIcon={<TestTube className="h-4 w-4" />}
              disabled={updateConfigMutation.isPending}
            >
              Testar
            </Button>
            <Button
              variant="outline"
              onClick={handleReset}
              leftIcon={<RotateCcw className="h-4 w-4" />}
              disabled={updateConfigMutation.isPending || resetConfigMutation.isPending}
            >
              Resetar
            </Button>
            <Button
              onClick={handleSave}
              leftIcon={<Save className="h-4 w-4" />}
              isLoading={updateConfigMutation.isPending}
            >
              Salvar
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto space-y-6 px-6">
        {/* Status */}
        {configData?.stats && configData.stats.email && (
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h2 className="text-lg font-medium mb-4 flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Status do Serviço
            </h2>
            <div className="flex items-center gap-3 p-3 rounded border">
              <Mail className="h-5 w-5 text-blue-500" />
              <div>
                <div className="font-medium">Email</div>
                <div className={`text-sm flex items-center gap-1 ${
                  configData.stats.email.configured ? 'text-green-600' : 'text-gray-500'
                }`}>
                  {configData.stats.email.configured ? (
                    <>
                      <CheckCircle className="h-3 w-3" />
                      Configurado
                    </>
                  ) : (
                    <>
                      <AlertCircle className="h-3 w-3" />
                      Não configurado
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Configuração de Email */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Configuração de Email
            </h2>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={config.email.enabled}
                onChange={(e) => updateEmailConfig('enabled', e.target.checked)}
                className="rounded border-gray-300"
              />
              <span className="text-sm">Habilitado</span>
            </label>
          </div>

          {config.email.enabled && (
            <div className="space-y-4">
              <Select
                label="Provedor de Email"
                value={selectedEmailProvider}
                onChange={(e) => handleEmailProviderChange(e.target.value)}
                options={[
                  { value: 'gmail', label: 'Gmail' },
                  { value: 'outlook', label: 'Outlook/Hotmail' },
                  { value: 'yahoo', label: 'Yahoo' },
                  { value: 'sapo', label: 'SAPO' },
                  { value: 'gmail_business', label: 'Gmail Business' },
                  { value: 'custom', label: 'Personalizado' }
                ]}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Servidor SMTP *"
                  value={config.email.host}
                  onChange={(e) => updateEmailConfig('host', e.target.value)}
                  placeholder="smtp.gmail.com"
                />
                <Input
                  label="Porta *"
                  type="number"
                  value={config.email.port}
                  onChange={(e) => updateEmailConfig('port', parseInt(e.target.value) || 587)}
                />
                <Input
                  label="Usuário/Email *"
                  type="email"
                  value={config.email.user}
                  onChange={(e) => updateEmailConfig('user', e.target.value)}
                  placeholder="seu@email.com"
                />
                <div className="relative">
                  <Input
                    label="Senha *"
                    type={showPasswords ? 'text' : 'password'}
                    value={config.email.password}
                    onChange={(e) => updateEmailConfig('password', e.target.value)}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPasswords(!showPasswords)}
                    className="absolute right-3 top-8 text-gray-400 hover:text-gray-600"
                  >
                    {showPasswords ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <Input
                  label="Nome do Remetente *"
                  value={config.email.fromName}
                  onChange={(e) => updateEmailConfig('fromName', e.target.value)}
                  placeholder="NexaMakers OS"
                />
                <Input
                  label="Email do Remetente *"
                  type="email"
                  value={config.email.fromEmail}
                  onChange={(e) => updateEmailConfig('fromEmail', e.target.value)}
                  placeholder="noreply@empresa.pt"
                />
              </div>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={config.email.secure}
                  onChange={(e) => updateEmailConfig('secure', e.target.checked)}
                  className="rounded border-gray-300"
                />
                <span className="text-sm">Usar SSL/TLS (porta 465)</span>
              </label>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Teste */}
      <Modal
        isOpen={showTestModal}
        onClose={() => setShowTestModal(false)}
        title="Testar Configuração de Email"
        size="md"
      >
        <div className="space-y-4">
          <Input
            label="Email de Teste"
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            placeholder="teste@email.com"
          />

          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowTestModal(false)}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleTest}
              isLoading={testConfigMutation.isPending}
              leftIcon={<TestTube className="h-4 w-4" />}
            >
              Executar Teste
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default EmailPage